// 같은 계열의 형제 사이트를 고른다.
//
// 국내(IWINV)는 antsnest.co.kr, 헤츠너는 antsnest.kr 로 나뉘어 있고 서로
// 백엔드가 다르다. 헤츠너에서 연 화면이 co.kr 을 부르면 안 된다 — 다른 쪽
// 시스템을 건드리는 셈이고, 로그인 팝업은 결과를 돌려줄 부모를 못 찾는다.
//
// talk 주소를 박아 두면 antsnest.kr 에서도 talk.antsnest.co.kr 이 열려,
// 팝업은 co.kr 로 메시지를 보내고 antsnest.kr 부모창은 아무것도 못 받는다.
// 실제로 그래서 "antsnest.kr 에서 로그인이 안 된다" 가 됐다.
export const FAMILIES = Object.freeze(['antsnest.kr', 'antsnest.co.kr']);

/** 이 host 가 속한 계열. 우리 도메인이 아니면 null. */
export function familyOf(hostname) {
  const host = String(hostname || '').toLowerCase();
  return FAMILIES.find((domain) => host === domain || host.endsWith('.' + domain)) || null;
}

/**
 * 지금 보고 있는 화면과 같은 계열의 형제 주소.
 *
 * 우리 도메인이 아니면(로컬 개발 등) co.kr 을 쓴다 — 운영 SSO 가 거기 있다.
 */
export function siblingOrigin(sub, location = window.location) {
  const family = familyOf(location.hostname) || 'antsnest.co.kr';
  return `https://${sub ? sub + '.' : ''}${family}`;
}

// antsnest.kr 에 실제로 있는 하위 도메인. 여기 없는 것을 계열 따라 바꾸면
// DNS 가 없어 링크가 죽는다 — app 과 malbok 이 그렇다.
// 개발(헤츠너)에도 실제로 떠 있고, DNS 와 인증서까지 맞는 것들.
//
// 짐작으로 적으면 안 된다 — 여기 적힌 것은 .kr 로 보내는데 그쪽이 죽어
// 있으면 링크가 그냥 안 열린다. music·file·town 이 그랬다. nginx 는 받는데
// 인증서에 이름이 없어 브라우저만 막혔고, 서버에서는 200 이라 멀쩡해 보였다.
// 2026-09-20 에 하나씩 눌러 보고 맞춘 목록이다.
const ON_HETZNER = Object.freeze([
  'talk', 'story', 'cast', 'store', 'play', 'estimate', 'meta', 'wbs',
  'edu', 'market', 'coin', 'archive', 'game',
]);

// 계열마다 주소 이름이 다른 것. 기준정보관리가 그렇다 —
// 운영은 app.antsnest.co.kr 인데 개발은 mdm.antsnest.kr 이다.
// app.antsnest.kr 은 DNS 개별 레코드가 없어 와일드카드로 빠지고,
// mdm.antsnest.kr 만 개발 서버를 가리킨다.
const SUB_BY_FAMILY = Object.freeze({
  app: { 'antsnest.kr': 'mdm' },
});

/**
 * 지금 보고 있는 것이 개발 환경인가.
 *
 * 개발은 두 가지 모습으로 온다.
 *   antsnest.kr 계열        헤츠너
 *   dev.* 로 시작하는 주소   dev.nodemaster.co.kr, dev.skyscribe.co.kr
 *
 * 운영과 생김새가 같아서, 개발에서 만진 것을 운영에서 한 줄 알고 넘어가기
 * 쉽다. 화면에 분명히 띄워야 한다.
 */
export function isDevEnvironment(location = window.location) {
  const host = String(location.hostname || '').toLowerCase();
  if (host === 'localhost' || host === '127.0.0.1') return true;
  if (host.startsWith('dev.')) return true;
  return familyOf(host) === 'antsnest.kr';
}

/** 화면에 띄울 환경 이름. 운영이면 null — 아무것도 띄우지 않는다. */
export function environmentLabel(location = window.location) {
  const host = String(location.hostname || '').toLowerCase();
  if (host === 'localhost' || host === '127.0.0.1') return '로컬';
  if (!isDevEnvironment(location)) return null;
  return '개발';
}

/**
 * 서비스 링크. 헤츠너에서 보면 헤츠너 것을, 국내에서 보면 국내 것을 연다.
 *
 * 헤츠너에 그 하위 도메인이 없으면 국내 주소를 그대로 쓴다. 억지로 바꾸면
 * 링크가 죽는다.
 */
export function serviceUrl(sub, location = window.location) {
  const family = familyOf(location.hostname) || 'antsnest.co.kr';
  const alias = SUB_BY_FAMILY[sub]?.[family];
  if (alias) return `https://${alias}.${family}`;
  if (family === 'antsnest.kr' && !ON_HETZNER.includes(sub)) {
    return `https://${sub}.antsnest.co.kr`;
  }
  return `https://${sub}.${family}`;
}

// 앤츠네스트 도메인이 아닌 서비스들. 개발본은 dev.<도메인> 으로 뜬다.
// 여기 없는 것은 개발본이 없어 운영 주소를 그대로 쓴다.
const DEV_PREFIXED = Object.freeze([
  'nodemaster.co.kr',
  'skyscribe.co.kr',
  'skynet.re.kr',
  'file.skynet.re.kr',
]);

/**
 * 앤츠네스트 밖 서비스의 주소.
 *
 * 개발에서 보면 dev.<도메인> 을, 운영에서 보면 그대로 연다.
 *
 * 개발본이 없는 것(minigrow.kr 등)은 운영 주소를 그대로 쓴다 — 억지로
 * dev. 를 붙이면 DNS 가 없어 링크가 죽는다. 반대로 개발본이 있는데 목록에서
 * 빠뜨리면 개발에서 운영을 열게 된다. skynet.re.kr 이 그랬다.
 *
 * file.skynet.re.kr 은 dev.file.skynet.re.kr 이다. 도메인 앞이 아니라
 * 호스트 전체 앞에 dev. 가 붙는다(file.dev.skynet.re.kr 이 아니다).
 */
export function externalServiceUrl(host, location = window.location) {
  const clean = String(host || '').replace(/^https?:\/\//, '').replace(/\/$/, '');
  if (!isDevEnvironment(location)) return `https://${clean}`;
  return DEV_PREFIXED.includes(clean) ? `https://dev.${clean}` : `https://${clean}`;
}

/** 팝업이 보낸 메시지를 받아도 되는 출처인가. */
export function isTrustedOrigin(origin) {
  let url;
  try {
    url = new URL(origin);
  } catch {
    return false;
  }
  if (url.protocol !== 'https:') return false;
  // 앞뒤 문자열 비교로 하면 antsnest.kr.evil.com 이 통과한다.
  return familyOf(url.hostname) !== null;
}
